import { type FC, type MouseEvent, type ReactNode, useState } from "react";
import {
  HeaderCellTitle,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "~/common/components/Table/TableElements";
import { type OrderbookRowItem } from "~/features/orderbook/models/orderbookModels";
import OrderbookResultsTableRow from "~/features/orderbook/components/OrderbookResultsTableRow";
import { SortDirection, useSorting } from "~/common/hooks/useSorting";
import { PopperTooltip } from "~/common/components/Tooltip";

import classes from "~/features/orderbook/components/OrderbookResultsTable.module.less";
import classNames from "classnames";
import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import { useTranslation } from "react-i18next";

interface Props {
  rows: OrderbookRowItem[];
  disableSorting: boolean;
}

enum SortKinds {
  SalesOrderNo,
  PurchaseOrderNo,
  ProductDesignation,
  Packaging,
  Status,
  ConfirmedDeliveryDate,
  ConfirmedQuantity,
  StockReserved,
  RequestedDeliveryDate,
  Account,
}

const OrderbookResultsTable: FC<Props> = (props) => {
  const { t } = useTranslation();
  const [sortCode, setSortCode] = useState(SortKinds.PurchaseOrderNo);
  const [sortDirection, setSortDirection] = useState(SortDirection.Desc);

  const rows = useSorting(props.rows, SORT_HANDLERS, sortCode, sortDirection);

  const createSortHandler = (codeToSet: SortKinds) => (e: MouseEvent<unknown>) => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: SortKinds) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
    disabled: props.disableSorting,
  });

  const headerT = (key: string) => t(`page.abmonitor.tableHeader.${key}`);

  const makeHeaderCell = (header: ReactNode, subheader: ReactNode, sortKind?: SortKinds, testId?: string) => {
    let cell = <HeaderCellTitle header={header} subheader={subheader} />;
    if (sortKind !== undefined) {
      cell = (
        <TableSortLabel
          {...createTableProps(sortKind)}
          data-test-id={testId}
          classes={{ icon: classes.sortHeaderIcon }}
        >
          {cell}
        </TableSortLabel>
      );
    }

    if (props.disableSorting) {
      cell = <PopperTooltip tooltipContent={t("spinner.text")}>{cell}</PopperTooltip>;
    }

    return <TableCell>{cell}</TableCell>;
  };

  return (
    <Table className={classNames("multi-body", classes.table)}>
      <TableHead>
        <TableRow>
          {makeHeaderCell(
            headerT("salesOrderNo"),
            headerT("orderLineNo"),
            SortKinds.SalesOrderNo,
            "abmonitor-sort-so-no",
          )}
          {makeHeaderCell(headerT("purchaseOrderNo"), undefined, SortKinds.PurchaseOrderNo, "abmonitor-sort-po-no")}
          {makeHeaderCell(headerT("status"), undefined, SortKinds.Status, "abmonitor-sort-status")}
          {isMultiAccountOrderSearchEnabled() &&
            makeHeaderCell(headerT("account"), headerT("endCustomer"), SortKinds.Account, "abmonitor-sort-account")}
          {makeHeaderCell(headerT("product"), undefined, SortKinds.ProductDesignation, "abmonitor-sort-designation")}
          {makeHeaderCell(headerT("packaging"), headerT("shipment"))}
          {makeHeaderCell(
            headerT("quantity"),
            headerT("previously"),
            SortKinds.ConfirmedQuantity,
            "abmonitor-sort-quantity",
          )}
          {makeHeaderCell(headerT("netPrice"), headerT("priceUnit"))}
          {makeHeaderCell(headerT("netValue"), undefined)}
          {makeHeaderCell(
            headerT("deliveryDate"),
            headerT("previously"),
            SortKinds.ConfirmedDeliveryDate,
            "abmonitor-sort-confirmed-delivery-date",
          )}
          {makeHeaderCell(
            headerT("reqDeliveryDate"),
            undefined,
            SortKinds.RequestedDeliveryDate,
            "abmonitor-sort-requested-delivery-date",
          )}
        </TableRow>
      </TableHead>

      {rows.map((rowData) => (
        <OrderbookResultsTableRow key={getKey(rowData)} item={rowData} />
      ))}
    </Table>
  );
};

export default OrderbookResultsTable;

const getKey = (item: OrderbookRowItem) => `${item.id}`;

const SORT_HANDLERS: Dict<(a: OrderbookRowItem, b: OrderbookRowItem) => number> = {
  [SortKinds.SalesOrderNo]: (a, b) => a.customerOrderNo.localeCompare(b.customerOrderNo),
  [SortKinds.PurchaseOrderNo]: (a, b) => a.schaefflerOrderNo.localeCompare(b.schaefflerOrderNo),
  [SortKinds.ProductDesignation]: (a, b) => a.product.designation.localeCompare(b.product.designation),
  [SortKinds.Packaging]: (a, b) => a.packSize - b.packSize,
  [SortKinds.Status]: (a, b) => {
    let res = +a.isNew - +b.isNew;
    if (res === 0) {
      res = +a.isUpdated - +b.isUpdated;
    }
    return res;
  },
  [SortKinds.ConfirmedDeliveryDate]: (a, b) => {
    if (!a.curDeliveryDate) {
      return -1;
    }
    if (!b.curDeliveryDate) {
      return 1;
    }
    return a.curDeliveryDate.getTime() - b.curDeliveryDate.getTime();
  },
  [SortKinds.ConfirmedQuantity]: (a, b) => a.curQuantity - b.curQuantity,
  [SortKinds.StockReserved]: (a, b) => a.stockReserved - b.stockReserved,
  [SortKinds.RequestedDeliveryDate]: (a, b) => {
    if (!a.requestedDeliveryDate) {
      return -1;
    }
    if (!b.requestedDeliveryDate) {
      return 1;
    }
    return a.requestedDeliveryDate.getTime() - b.requestedDeliveryDate.getTime();
  },
  [SortKinds.Account]: (a, b) => {
    let result = a.account?.name?.localeCompare(b.account?.name ?? "");
    if (!result) {
      result = a.account?.endCustomerName?.localeCompare(b.account?.endCustomerName ?? "");
    }
    return result ?? 0;
  },
};
